import {
  DialogCard,
  DialogFooter,
  DialogHeader,
  DialogSubtitle,
  DialogTitle,
  PrimaryButton,
  SecondaryButton,
} from '@src/components/common/dialogStyles';
import Modal from '@src/components/common/Modal';
import React, { useState } from 'react';
import styled from 'styled-components';

/**
 * PLAT-11059 DS2 / PLAT-11064 DS5 — where a row-level naming run is specified.
 *
 * Two jobs in one dialog. It is the ask before a generated name replaces one the
 * creator typed (DE10), and it is where they say in their own words what they
 * want instead — the same "structured header plus a free-text steer, one primary
 * action" shape as `AutoChapterOverlay`, so this is a dialog a creator has met
 * before rather than a second idiom for the same kind of request.
 *
 * The scaffold is the shared one (`dialogStyles`), not `ConfirmModal`: a confirm
 * dialog is a yes/no with no room for a field, and this one takes input.
 *
 * Presentation and local form state only — it returns the guidance and knows
 * nothing about the request, the prompt or the reel.
 */

interface ReelNameGenerationDialogProps {
  /** Which heading is being named — the copy is the only difference. */
  kind: 'section' | 'chapter';
  /** The name on it now, quoted so the creator can see what they stand to lose. */
  currentName?: string;
  /**
   * Why the dialog is open. `'confirm'` (the default) is the ask a generation
   * owes a name the creator typed — the subtitle names what is at stake.
   * `'guide'` is the creator having asked for this box themselves (the
   * Transcript tab's "…and Instruction", PLAT-11257), where a warning about a
   * replacement they just requested would be answering a question nobody asked.
   */
  intent?: 'confirm' | 'guide';
  onClose: () => void;
  /** `undefined` guidance is a plain run: the prompt reads the passage alone. */
  onGenerate: (guidance?: string) => void;
}

export const ReelNameGenerationDialog: React.FC<
  ReelNameGenerationDialogProps
> = ({ kind, currentName, intent = 'confirm', onClose, onGenerate }) => {
  const [guidance, setGuidance] = useState('');

  const noun = kind === 'section' ? 'clip' : 'chapter';
  const run = () => onGenerate(guidance.trim() || undefined);

  return (
    <Modal isOpen closeModal={onClose}>
      <DialogCard $size="sm" data-testid="reel-name-generation-dialog">
        <DialogHeader>
          <DialogTitle>
            {kind === 'section'
              ? 'Generate a Clip Name'
              : 'Generate a Chapter Name'}
          </DialogTitle>
          {/* What is lost, named rather than described: the creator typed it,
              and seeing it is what makes cancelling an informed choice. The
              dialog only ever opens over a name the creator typed (DS2), so
              both readings say a replacement is coming — the fallback is for
              the one shape that has no name to quote, a `'user'` name typed
              blank, and it must not read as though nothing is at stake. */}
          <DialogSubtitle>
            {intent === 'guide'
              ? currentName
                ? `This replaces “${currentName}”.`
                : `Say what the new name for this ${noun} should say.`
              : currentName
                ? `This replaces “${currentName}”, the name you typed.`
                : `This replaces the name you typed on this ${noun}.`}
          </DialogSubtitle>
        </DialogHeader>

        <Field>
          <FieldLabel htmlFor="reel-name-guidance">
            What should the name say? (optional)
          </FieldLabel>
          <GuidanceInput
            id="reel-name-guidance"
            rows={3}
            value={guidance}
            autoFocus
            placeholder="e.g. name the company, three words at most"
            data-testid="reel-name-guidance"
            onChange={e => setGuidance(e.target.value)}
            // Enter runs it; a newline in a one-line steer is worth nothing,
            // and the field is the only thing to fill in.
            onKeyDown={e => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                run();
              }
            }}
          />
          <FieldHint>
            Leave it empty and the AI decides what the {noun} is about.
          </FieldHint>
        </Field>

        <DialogFooter>
          <SecondaryButton
            type="button"
            data-testid="reel-name-generation-cancel"
            onClick={onClose}
          >
            {currentName ? 'Keep My Name' : 'Cancel'}
          </SecondaryButton>
          <PrimaryButton
            type="button"
            data-testid="reel-name-generation-run"
            onClick={run}
          >
            Generate Name
          </PrimaryButton>
        </DialogFooter>
      </DialogCard>
    </Modal>
  );
};

const Field = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const FieldLabel = styled.label`
  font-size: var(--text-sm);
  font-weight: var(--font-weight-medium);
  color: var(--foreground);
`;

const FieldHint = styled.span`
  font-size: var(--text-xs);
  color: var(--text-muted);
`;

const GuidanceInput = styled.textarea`
  width: 100%;
  padding: 8px;
  border: 1px solid var(--border);
  border-radius: var(--radius-input);
  background-color: var(--input-background);
  color: var(--foreground);
  font-family: var(--font-primary);
  font-size: var(--text-sm);
  resize: vertical;

  &:focus {
    outline: none;
    border-color: var(--focus-border);
    box-shadow: 0 0 0 1px var(--focus-ring);
  }

  &::placeholder {
    color: var(--text-muted);
  }
`;
